import { Component, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import {
  DynamicDialogRef,
  DynamicDialogConfig,
} from 'primeng/dynamicdialog';
import { ExternalClientTypeService } from '../../services/externalclienttype.service';
import { Subject, takeUntil, finalize } from 'rxjs';

@Component({
  selector: 'app-externalclienttype-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    <form [formGroup]="form" class="dialog-form">
      <div class="form-field">
        <label for="name" class="form-label">Nome *</label>
        <input
          pInputText
          id="name"
          type="text"
          formControlName="name"
          placeholder="Nome do tipo de cliente externo"
          class="form-input"
          autofocus
        />
        <small *ngIf="isFieldInvalid('name')" class="error-text">
          O nome é obrigatório.
        </small>
      </div>

      <div class="form-field">
        <label for="description" class="form-label">Descrição</label>
        <textarea
          pInputText
          id="description"
          formControlName="description"
          placeholder="Descrição (opcional)"
          class="form-input"
          rows="3"
        ></textarea>
      </div>

      <div class="dialog-actions">
        <p-button
          label="Cancelar"
          severity="secondary"
          [outlined]="true"
          (onClick)="onCancel()"
        ></p-button>
        <p-button
          label="Salvar"
          icon="pi pi-check"
          (onClick)="onSave()"
          [disabled]="form.invalid || loading"
          [loading]="loading"
        ></p-button>
      </div>
    </form>
  `,
  styles: [`
    .dialog-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding-top: 8px;
    }

    .form-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .form-label {
      font-weight: 600;
      font-size: 0.9rem;
      color: var(--text-color, #333);
    }

    .form-input {
      width: 100%;
    }

    .error-text {
      color: var(--red-500, #ef4444);
      font-size: 0.8rem;
    }

    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 8px;
    }
  `],
})
export class ExternalClientTypeDialogComponent implements OnDestroy {
  form: FormGroup;
  loading = false;
  private destroyed$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private service: ExternalClientTypeService,
    private messageService: MessageService,
    public dialogRef: DynamicDialogRef,
    public dialogConfig: DynamicDialogConfig
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      description: ['', [Validators.maxLength(500)]],
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  isFieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    const request = {
      name: this.form.value.name.trim(),
      description: this.form.value.description?.trim() || '',
    };

    this.service
      .create(request)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (created) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: `Tipo "${created.name}" criado com sucesso.`,
            life: 3000,
          });
          this.dialogRef.close(created);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao criar o tipo de cliente externo.',
            life: 5000,
          });
        },
      });
  }
}
